import React from "react";
import { FaPlus, FaRedo } from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import AdherentCreateDrawer from "./components/AdherentCreateDrawer";
import AdherentStatusModal from "./components/AdherentStatusModal";
import CoachGroupsHero from "./components/CoachGroupsHero";
import CoachLimitsCard from "./components/CoachLimitsCard";
import GroupCard from "./components/GroupCard";
import GroupFormModal from "./components/GroupFormModal";
import GroupsEmptyState from "./components/GroupsEmptyState";
import GroupsSkeleton from "./components/GroupsSkeleton";
import InviteModal from "./components/InviteModal";
import MessageModal from "./components/MessageModal";
import AdherentEditDrawer from "./components/AdherentEditDrawer";

import { useGroupsPage } from "./hooks/useGroupsPage";

function GroupsPage() {
  useScrollToTop();

  const {
    user,
    userRole,
    userLimits,
    groups,
    adherentsData,
    selectedGroup,
    selectedAdherent,
    inviteLink,
    messageDraft,
    messageContext,
    modals,
    loading,
    refreshing,
    savingAction,
    stats,
    fetchGroups,
    openCreateModal,
    openEditModal,
    openCreateAdherentDrawer,
    closeCreateModal,
    closeEditModal,
    closeCreateAdherentDrawer,
    closeInviteModal,
    closeMessageModal,
    closeStatusModal,
    handleCreateGroup,
    handleUpdateGroup,
    handleCreateAdherentInGroup,
    handleDeleteGroup,
    handleInviteGroup,
    handleOpenStatusModal,
    handleApproveAdherent,
    handleRejectAdherent,
    handleDeleteAdherent,
    handleOpenMessageModal,
    handleMessageDraftChange,
    handleSendMessage,
    openEditAdherentDrawer,
    closeEditAdherentDrawer,
    handleUpdateAdherentInGroup,
  } = useGroupsPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <CoachGroupsHero stats={stats} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                Espace coach
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Gestion des groupes
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Crée tes groupes d'entraînement, invite tes judokas par lien ou
                QR code, valide les demandes d'inscription, ajoute toi-même des
                adhérents et contacte les responsables directement depuis ton
                espace coach.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[420px]">
              <button
                type="button"
                onClick={fetchGroups}
                disabled={refreshing}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-sky-300/40 dark:hover:text-sky-100"
              >
                <FaRedo className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Actualisation..." : "Actualiser"}
              </button>

              <button
                type="button"
                onClick={openCreateModal}
                disabled={!stats.canCreateGroup}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
              >
                <FaPlus />
                Nouveau groupe
              </button>
            </div>
          </div>

          <CoachLimitsCard
            user={user}
            userRole={userRole}
            userLimits={userLimits}
            stats={stats}
          />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {loading ? (
          <GroupsSkeleton />
        ) : groups.length > 0 ? (
          <div className="grid gap-5 lg:grid-cols-2">
            {groups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                adherents={adherentsData[group.id] || []}
                savingAction={savingAction}
                onInvite={() => handleInviteGroup(group)}
                onEdit={() => openEditModal(group)}
                onDelete={() => handleDeleteGroup(group)}
                onCreateAdherent={() => openCreateAdherentDrawer(group)}
                onOpenStatus={(adherent) =>
                  handleOpenStatusModal(adherent, group)
                }
                onMessageAdherent={(adherent) =>
                  handleOpenMessageModal([adherent], false, group)
                }
                onMessageGroup={() =>
                  handleOpenMessageModal(
                    adherentsData[group.id] || [],
                    true,
                    group,
                  )
                }
                onEditAdherent={(adherent) =>
                  openEditAdherentDrawer(adherent, group)
                }
              />
            ))}
          </div>
        ) : (
          <GroupsEmptyState onCreate={openCreateModal} />
        )}
      </section>

      <GroupFormModal
        mode="create"
        isOpen={modals.create}
        saving={savingAction === "create-group"}
        onClose={closeCreateModal}
        onSubmit={handleCreateGroup}
      />

      <GroupFormModal
        mode="edit"
        isOpen={modals.edit}
        group={selectedGroup}
        saving={savingAction === "update-group"}
        onClose={closeEditModal}
        onSubmit={handleUpdateGroup}
      />

      <AdherentCreateDrawer
        isOpen={modals.createAdherent}
        group={selectedGroup}
        user={user}
        saving={savingAction === "create-adherent"}
        onClose={closeCreateAdherentDrawer}
        onSubmit={handleCreateAdherentInGroup}
      />

      <InviteModal
        isOpen={modals.invite}
        group={selectedGroup}
        inviteLink={inviteLink}
        onClose={closeInviteModal}
      />

      <MessageModal
        isOpen={modals.message}
        draft={messageDraft}
        context={messageContext}
        saving={savingAction === "send-message"}
        onChange={handleMessageDraftChange}
        onSend={handleSendMessage}
        onClose={closeMessageModal}
      />

      <AdherentStatusModal
        isOpen={modals.status}
        adherent={selectedAdherent}
        group={selectedGroup}
        savingAction={savingAction}
        onApprove={handleApproveAdherent}
        onReject={handleRejectAdherent}
        onDelete={handleDeleteAdherent}
        onClose={closeStatusModal}
      />

      <AdherentEditDrawer
        isOpen={modals.editAdherent}
        adherent={selectedAdherent}
        group={selectedGroup}
        groups={groups}
        saving={savingAction === "update-adherent"}
        onClose={closeEditAdherentDrawer}
        onSubmit={handleUpdateAdherentInGroup}
      />

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default GroupsPage;
